import { useCallback, useEffect, useState } from 'react';
import { formatCertificateCode, type AltegioTodoItem, type PrintBatchView } from '@360face/domain';
import { ApiError } from '../api/client';
import { BRAND } from '../brand';
import type { CertificateStock, StaffApi } from './api';
import { tabletMoney, type TabletLocale, type TabletMessages } from './messages';

/**
 * Gift certificates on the tablet (owner 15.10.2026):
 *  - what still has to be repeated in Altegio by hand (sales and payments), with "Entered";
 *  - printed numbers on the shelf / sold, new print batches and a printable A4 sheet.
 */
export function Certificates({
  api,
  t,
  locale,
  onBack,
}: {
  api: StaffApi;
  t: TabletMessages;
  locale: TabletLocale;
  onBack: () => void;
}) {
  const [todo, setTodo] = useState<AltegioTodoItem[]>();
  const [stock, setStock] = useState<CertificateStock>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [values, setValues] = useState<number[]>();
  const [count, setCount] = useState(20);
  const [pin, setPin] = useState('');
  const [sheet, setSheet] = useState<PrintBatchView>();

  const fail = useCallback(
    (e: unknown) => setError(t.errors[e instanceof ApiError ? e.code : 'default'] ?? t.errors.default),
    [t],
  );
  const load = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([api.altegioTodo(), api.certificateStock()]);
      setTodo(a);
      setStock(s);
      setValues((v) => v ?? s.rows.map((r) => r.valueMinor));
    } catch (e) {
      fail(e);
    }
  }, [api, fail]);
  useEffect(() => {
    void load();
  }, [load]);

  const run = async (f: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    try {
      await f();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const money = (amountMinor: number) => tabletMoney({ amountMinor, currency: 'EUR' }, locale);
  const when = (iso: string) =>
    new Date(iso).toLocaleString(locale === 'en' ? 'en-GB' : locale === 'ru' ? 'ru-RU' : 'pt-PT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  const dmy = (d: string) => d.split('-').reverse().join('/');

  if (sheet) return <PrintSheet sheet={sheet} t={t} onBack={() => setSheet(undefined)} />;

  return (
    <div className="tablet detail certs">
      <header className="t-bar">
        <button className="t-ghost" onClick={onBack}>
          {t.back}
        </button>
        <strong>{t.certsTitle}</strong>
      </header>
      {error && (
        <p className="t-warn" role="alert">
          {error}
        </p>
      )}

      <section className="t-card">
        <h3>
          {t.certsAltegio} {todo && todo.length > 0 && <span className="t-dot">{todo.length}</span>}
        </h3>
        <p className="t-hint">{t.certsAltegioHint}</p>
        {todo?.length === 0 && <p className="t-info">{t.certsAltegioEmpty}</p>}
        <ul className="c-todo">
          {todo?.map((i) => (
            <li key={`${i.code}-${i.kind}-${i.visitId ?? ''}-${i.at}`}>
              <div>
                <span className={`c-kind ${i.kind}`}>{i.kind === 'sale' ? t.certsSale : t.certsUse}</span>{' '}
                <strong className="c-code">{formatCertificateCode(i.code)}</strong> ·{' '}
                <strong>{money(i.amountMinor)}</strong>
                <br />
                <small>
                  {when(i.at)}
                  {i.kind === 'redemption' && ` · ${t.certsBalance(money(i.balanceMinor))}`}
                  {i.expiresOn && ` · ${t.certsUntil(dmy(i.expiresOn))}`}
                </small>
                <br />
                <small className="t-hint">{i.kind === 'sale' ? t.certsSaleHow : t.certsUseHow}</small>
              </div>
              <button
                className="t-primary"
                disabled={busy}
                onClick={() =>
                  void run(async () =>
                    setTodo(
                      await api.markAltegio(
                        i.code,
                        i.kind === 'sale' ? { kind: 'sale' } : { kind: 'redemption', visitId: i.visitId! },
                      ),
                    ),
                  )
                }
              >
                ✓ {t.certsMarked}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="t-card">
        <h3>{t.certsStock}</h3>
        {stock && (
          <table className="c-stock">
            <thead>
              <tr>
                <th>{t.certsValue}</th>
                <th>{t.certsInStock}</th>
                <th>{t.certsSold}</th>
              </tr>
            </thead>
            <tbody>
              {stock.rows.map((r) => (
                <tr key={r.valueMinor}>
                  <td>{money(r.valueMinor)}</td>
                  <td>{r.inStock}</td>
                  <td>{r.sold}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {stock && stock.batches.length > 0 && (
          <>
            <h4>{t.certsBatches}</h4>
            <ul className="c-batches">
              {stock.batches.map((b) => (
                <li key={b.batchId}>
                  <span>
                    {when(b.createdAt)} · {t.certsBatchLeft(b.inStock)}
                  </span>
                  <button
                    disabled={busy || b.inStock === 0}
                    onClick={() => void run(async () => setSheet(await api.printBatch(b.batchId)))}
                  >
                    🖨 {t.certsPrint}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        <h4>{t.certsNewBatch}</h4>
        <div className="c-values">
          {stock?.rows.map((r) => {
            const on = values?.includes(r.valueMinor) ?? false;
            return (
              <button
                key={r.valueMinor}
                className={on ? 'on' : ''}
                aria-pressed={on}
                onClick={() =>
                  setValues((v) => (on ? (v ?? []).filter((x) => x !== r.valueMinor) : [...(v ?? []), r.valueMinor]))
                }
              >
                {money(r.valueMinor)}
              </button>
            );
          })}
        </div>
        <div className="c-batch-form">
          <label className="t-pin">
            {t.certsCount}
            <input
              inputMode="numeric"
              value={count}
              onChange={(e) => setCount(Math.min(50, Number(e.target.value.replace(/\D/g, '').slice(0, 2)) || 0))}
            />
          </label>
          <label className="t-pin">
            {t.pin}
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            />
          </label>
          <button
            className="t-primary"
            disabled={busy || !values?.length || count < 1 || pin.length < 4}
            onClick={() =>
              void run(async () => {
                const b = await api.createPrintBatch(values!, count, pin);
                setPin('');
                await load();
                setSheet(b);
              })
            }
          >
            {t.certsCreate}
          </button>
        </div>
        <p className="t-hint">{t.certsAltegioStockHint(count)}</p>
      </section>
    </div>
  );
}

/** A4 sheet, 8 cards of A7 (105 × 74 mm) to cut. Only numbers still on the shelf. */
function PrintSheet({ sheet, t, onBack }: { sheet: PrintBatchView; t: TabletMessages; onBack: () => void }) {
  const pages: PrintBatchView['certificates'][] = [];
  for (let i = 0; i < sheet.certificates.length; i += 8) pages.push(sheet.certificates.slice(i, i + 8));
  return (
    <div className="tablet detail print-view">
      <header className="t-bar no-print">
        <button className="t-ghost" onClick={onBack}>
          {t.back}
        </button>
        <strong>{t.certsBatchLeft(sheet.certificates.length)}</strong>
        <button className="t-primary" onClick={() => window.print()}>
          🖨 {t.certsPrintNow}
        </button>
      </header>
      <div className="print-sheet">
        {pages.map((page, p) => (
          <div className="print-page" key={p}>
            {page.map((c) => (
              <div className="print-card" key={c.code}>
                <img src={BRAND.logo} alt="360°FACE" />
                <p className="pc-title">
                  {t.printGift}
                  <br />
                  <span>{t.printGiftEn}</span>
                </p>
                <p className="pc-value">{c.valueMinor / 100} €</p>
                <p className="pc-line">{t.printFor}: ______________________</p>
                <p className="pc-line">{t.printFrom}: ______________________</p>
                <p className="pc-no">
                  {t.printNo} <strong>{formatCertificateCode(c.code)}</strong>
                </p>
                <p className="pc-valid">{t.printValid(sheet.validityMonths)} · 360face.pt</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
